import { of, switchMap, EMPTY, distinctUntilChanged, tap, merge, delay, first, catchError, finalize, BehaviorSubject, map, fromEvent, startWith } from 'rxjs';

import { replaceViewContainer, renderTemplate } from './view.js';
import { jobsQueueLength$, myJobs$, archiveJob, fetchImg, updateJobFlag } from './api.js';
import { handleErrors } from './errors.js';

export function createMyJobsView() {
    return of(undefined).pipe(
        switchMap(() => {
            let root = renderTemplate('my-jobs-view');
            replaceViewContainer(root);

            let nsfwShownEl = root.querySelector('.nsfw-toggle');
            let nsfwShown$ = fromEvent(nsfwShownEl, 'change').pipe(
                startWith(undefined),
                map(() => nsfwShownEl.checked),
                distinctUntilChanged(),
            );

            let queueCounter$ = jobsQueueLength$.pipe(
                distinctUntilChanged(),
                tap(jobCount => root.querySelector('.queue-length').textContent = `${jobCount}`),
            );

            let jobElCache = new Map();
            let imageBlobUrls = new Map();

            let jobActions = [
                ['download', handleDownloadJobImage],
                ['archive', handleArchiveJob],
                ['fork', handleForkJob],
            ];
            let flags = ['nsfw', 'share'];

            let renderMyJobs$ = myJobs$.pipe(
                switchMap(jobs => {
                    let obs = [];
                    let container = root.querySelector('.jobs');
                    container.innerHTML = '';
                    for (let job of jobs) {
                        let jobEl, thumb;
                        if (jobElCache.has(job.id)) {
                            jobEl = jobElCache.get(job.id);
                            thumb = jobEl.querySelector('.thumb');
                        } else {
                            jobEl = renderTemplate('job-widget');
                            jobEl.setAttribute('data-job-id', `${job.id}`);
                            thumb = jobEl.querySelector('.thumb');
                            thumb.addEventListener('click', openImage, false);
                            let promptEl = jobEl.querySelector('.prompt');
                            promptEl.textContent = `✅ ${job.prompt}`;
                            if (job.negative_prompt) {
                                let negativePromptEl = jobEl.querySelector('.negative-prompt');
                                negativePromptEl.classList.remove('hidden');
                                negativePromptEl.textContent = `❌ ${job.negative_prompt}`;
                            }
                            for (let flag of flags) {
                                let flagEl = jobEl.querySelector(`.flags .${flag}`);
                                flagEl.setAttribute('data-flag', flag);
                                flagEl.checked = job[flag];
                                flagEl.addEventListener('change', persistFlagChanges, false);
                            }
                            for (let [className, handler] of jobActions) {
                                jobEl.querySelector(`.actions .${className}`).addEventListener('click', handler, false);
                            }
                            jobElCache.set(job.id, jobEl);
                        }
                        // TODO keep the observable subscribed even after refreshes
                        if (job.nsfw) {
                            obs.push(showHideNsfwJobEl(jobEl));
                        }
                        if (job.generated) {
                            obs.push(loadImg(thumb, `/thumb/${job.id}`));
                        } else {
                            thumb.src = 'generating.png';
                        }
                        let shareStatusEl = jobEl.querySelector('.share-status');
                        if (job.share_token) {
                            let shareLinkEl = jobEl.querySelector('.share-link');
                            shareLinkEl.href = `/share.html#${job.share_token}`;
                            let shareServesEl = jobEl.querySelector('.share-serves');
                            shareServesEl.textContent = `${job.share_serves}`;
                            shareStatusEl.classList.remove('hidden');
                        } else {
                            shareStatusEl.classList.add('hidden');
                        }
                        container.appendChild(jobEl);
                    }
                    return merge(...obs);
                }),
            );

            return merge(
                queueCounter$,
                renderMyJobs$,
            );

            function showHideNsfwJobEl(jobEl) {
                return nsfwShown$.pipe(
                    tap(nsfwShown => {
                        if (nsfwShown) {
                            jobEl.style.display = null;
                        } else {
                            jobEl.style.display = 'none';
                        }
                    }),
                );
            }

            function loadImg(imgEl, url) {
                let fetch$;
                if (imageBlobUrls.has(url)) {
                    fetch$ = imageBlobUrls.get(url);
                } else {
                    fetch$ = fetchImg(url);
                    imageBlobUrls.set(url, fetch$);
                }
                return fetch$.pipe(
                    catchError(err => {
                        console.warn(`Unable to load image ${url}:`, err);
                        return of('/no-image.png');
                    }),
                    tap(blobUrl => {
                        imgEl.src = blobUrl;
                    }),
                );
            }

            function openImage(event) {
                let jobId = getJobIdForElement(event.target);
                fetchImg(`/image/${jobId}`).pipe(
                    tap(blobUrl => window.open(blobUrl, '_blank')),
                    delay(100),
                    first(),
                    handleErrors(),
                ).subscribe();
            }

            function persistFlagChanges(event) {
                let jobId = getJobIdForElement(event.target);
                let flagName = getAttributeForElement(event.target, 'data-flag');
                event.target.disabled = true;
                let flagValue = event.target.checked;
                updateJobFlag(jobId, flagName, flagValue).pipe(
                    finalize(() => {
                        event.target.disabled = false;
                    }),
                    handleErrors(),
                ).subscribe();
            }

            function handleDownloadJobImage(event) {
                let jobId = getJobIdForElement(event.target);
                event.target.disabled = true;
                fetchImg(`/image/${jobId}`).pipe(
                    tap(blobUrl => {
                        let a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = `m3k-diffusion-${jobId}.png`;
                        a.click();
                    }),
                    delay(100),
                    first(),
                    handleErrors(),
                ).subscribe();
            }

            function handleArchiveJob(event) {
                let jobId = getJobIdForElement(event.target);
                event.target.disabled = true;
                archiveJob(jobId).pipe(
                    finalize(() => {
                        event.target.disabled = false;
                    }),
                    handleErrors(),
                ).subscribe();
            }

            function handleForkJob(event) {
                let jobId = getJobIdForElement(event.target);
                window.location = `#fork/${jobId}`;
            }

            function getJobIdForElement(el) {
                return getAttributeForElement(el, 'data-job-id');
            }

            function getAttributeForElement(el, attribute) {
                while (el) {
                    let value = el.getAttribute(attribute);
                    if (value) {
                        return value;
                    }
                    el = el.parentElement;
                }
                throw new Error(`Attribute ${attribute} not found in parent element chain`);
            }
        }),
    );
}
